// Modal zum Anlegen einer VA-Serie aus einer bereits gespeicherten,
// fehlerfreien Veranstaltung (24.09.2026, Nutzervorgabe).
//
// Jeder Block trägt nur die fünf Felder, die je Termin variieren
// (Datum+Uhrzeit, "Während der Geschäftszeiten", Anmeldefrist, Zahlungsfrist,
// Onlinestellung) — alles andere (Titel, VA-Typ, Depots, Preis, Zahlart,
// Beschreibung, ...) klont der Server 1:1 vom Quell-Event (siehe
// backend/create-event-series.ts). businessHours ist bewusst NICHT geklont
// (25.09.2026, Nutzervorgabe, korrigiert das ursprüngliche Design) — jeder
// Termin einer Serie darf unabhängig vom Quell-Event und von den anderen
// Terminen mit oder ohne feste Uhrzeit laufen.
// Verfügbar in jedem Status der Quell-VA außer "Nicht freigegeben"
// (seriesEnabled in EventFormDialog.tsx).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useCreateEventSeries } from '@/services/event-service';
import { ApiError } from '@/lib/api-client';
import type { DepotEvent, EventSeriesOccurrence } from '@/types';
import { combineDateTime, formatTimeInput, nextMondayOrToday, TIME_PATTERN } from './EventFormDialog';
import { isMonday } from './online-date';

interface SeriesBlock {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  /** Pro Termin wählbar (25.09.2026, Nutzervorgabe) — siehe Datei-Kommentar oben. */
  businessHours: boolean;
  registrationDeadline: string;
  paymentDeadline: string;
  plannedOnlineDate: string;
}

/** Startwert für einen neuen Termin — übernimmt businessHours vom Quell-Event
 *  nur als Vorbelegung, frei änderbar pro Termin (siehe Datei-Kommentar oben). */
function emptyBlock(businessHours: boolean): SeriesBlock {
  return {
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    businessHours,
    registrationDeadline: '',
    paymentDeadline: '',
    plannedOnlineDate: nextMondayOrToday(),
  };
}

/**
 * Same rules as EventFormDialog's validate() (D.3/D.7) — mirrored client-side,
 * re-checked server-side. Datum/Uhrzeit getrennte Fehlerschlüssel
 * (startDate/endDate vs. startTime/endTime, 25.09.2026, Nutzervorgabe) —
 * vorher teilten sich beide Zeilen dieselben Schlüssel (startAt/endAt), was
 * die Fehlermeldung an der falschen Zeile zeigen konnte.
 */
function validateBlock(
  source: DepotEvent,
  block: SeriesBlock,
  t: TFunction<'events'>,
  onlineDateMondayOnly: boolean
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!block.startDate) errors.startDate = t('form.errors.dateRequired');
  if (!block.endDate) errors.endDate = t('form.errors.dateRequired');

  // Uhrzeit wird nur geprüft, wenn dieser Termin (nicht das Quell-Event!)
  // nicht "während der Geschäftszeiten" läuft (25.09.2026, Nutzervorgabe).
  if (!block.businessHours) {
    if (!block.startTime) errors.startTime = t('form.errors.timeRequired');
    else if (!TIME_PATTERN.test(block.startTime)) errors.startTime = t('form.errors.timeInvalid');
    if (!block.endTime) errors.endTime = t('form.errors.timeRequired');
    else if (!TIME_PATTERN.test(block.endTime)) errors.endTime = t('form.errors.timeInvalid');
  }

  if (!block.plannedOnlineDate) errors.plannedOnlineDate = t('form.errors.plannedOnlineDateRequired');
  // Depot-Partner: Onlinestellung nur montags (03.11.2026). Jeder Termin einer Serie ist neu
  // angelegt, also gilt die Regel hier immer — kein "unverändert"-Fall wie beim Bearbeiten.
  else if (onlineDateMondayOnly && !isMonday(block.plannedOnlineDate)) {
    errors.plannedOnlineDate = t('form.errors.plannedOnlineDateNotMonday');
  }

  if (source.registrationRequired && !block.registrationDeadline) {
    errors.registrationDeadline = t('form.errors.registrationDeadlineRequired');
  } else if (
    source.registrationRequired &&
    block.registrationDeadline &&
    block.plannedOnlineDate &&
    block.registrationDeadline < block.plannedOnlineDate
  ) {
    errors.registrationDeadline = t('form.errors.deadlineBeforeOnlineDate');
  }

  if (source.costEnabled && !block.paymentDeadline) {
    errors.paymentDeadline = t('form.errors.paymentDeadlineRequired');
  } else if (
    source.costEnabled &&
    block.paymentDeadline &&
    block.plannedOnlineDate &&
    block.paymentDeadline < block.plannedOnlineDate
  ) {
    errors.paymentDeadline = t('form.errors.deadlineBeforeOnlineDate');
  }

  return errors;
}

/**
 * `businessHours` (25.09.2026, Nutzervorgabe) — jetzt eine echte
 * Pro-Termin-Einstellung (siehe Datei-Kommentar oben und
 * create-event-series.ts), NICHT mehr vom Quell-Event geklont. Bei aktivem
 * Switch bleibt die Uhrzeit leer (Mitternacht-Konvention, siehe
 * combineDateTime).
 */
function toOccurrence(block: SeriesBlock): EventSeriesOccurrence {
  return {
    startAt: combineDateTime(block.startDate, block.businessHours ? '' : block.startTime),
    endAt: combineDateTime(block.endDate, block.businessHours ? '' : block.endTime),
    businessHours: block.businessHours,
    plannedOnlineDate: combineDateTime(block.plannedOnlineDate, ''),
    registrationDeadline: block.registrationDeadline
      ? combineDateTime(block.registrationDeadline, '')
      : undefined,
    paymentDeadline: block.paymentDeadline ? combineDateTime(block.paymentDeadline, '') : undefined,
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: DepotEvent;
  /** Depot-Partner dürfen die Onlinestellung nur auf einen Montag legen. */
  onlineDateMondayOnly?: boolean;
}

export function EventSeriesDialog({ open, onOpenChange, source, onlineDateMondayOnly = false }: Props) {
  const { t } = useTranslation('events');
  const { toast } = useToast();
  const createSeries = useCreateEventSeries();

  const [blocks, setBlocks] = useState<SeriesBlock[]>([emptyBlock(source.businessHours)]);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  useEffect(() => {
    if (!open) return;
    setBlocks([emptyBlock(source.businessHours)]);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateBlock = (index: number, patch: Partial<SeriesBlock>) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  /**
   * Prüft die Onlinestellung eines Termins schon bei der Auswahl im Kalender
   * (03.11.2026), wie im Einzelformular: nach einem Klick im Datumsfenster bleibt der
   * Fokus im Feld, ein blur kommt also nicht. Betrifft nur dieses eine Feld des Termins;
   * Zwischenstände beim Tippen (leer, Jahr unter 1000) werden übergangen.
   */
  const checkOnlineDateOnPick = (index: number, value: string) => {
    if (!value || Number(value.slice(0, 4)) < 1000) return;

    const message = validateBlock(
      source,
      { ...blocks[index], plannedOnlineDate: value },
      t,
      onlineDateMondayOnly
    ).plannedOnlineDate;

    setErrors((prev) => {
      const block = { ...(prev[index] ?? {}) };
      if (message) block.plannedOnlineDate = message;
      else delete block.plannedOnlineDate;
      return { ...prev, [index]: block };
    });
  };

  const addBlock = () => setBlocks((prev) => [...prev, emptyBlock(source.businessHours)]);
  const removeBlock = (index: number) =>
    setBlocks((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const submit = async (createAs: 'initial' | 'in_review') => {
    const validated = blocks.map((b) => validateBlock(source, b, t, onlineDateMondayOnly));
    const hasErrors = validated.some((e) => Object.keys(e).length > 0);
    if (hasErrors) {
      setErrors(Object.fromEntries(validated.map((e, i) => [i, e])));
      return;
    }

    try {
      const result = await createSeries.mutateAsync({
        identifier: source.identifier,
        request: { occurrences: blocks.map(toOccurrence), createAs },
      });
      if (result.failed.length > 0) {
        toast({
          title: t('form.series.partialSuccess', { created: result.created.length, failed: result.failed.length }),
          variant: 'destructive',
        });
      } else {
        toast({ title: t('form.series.success', { count: result.created.length }) });
      }
      onOpenChange(false);
    } catch (e) {
      // A 400 from the real backend carries per-occurrence detail; the mock
      // branch throws a plain Error instead (see createMockEventSeries) — both
      // land here as a flat toast, since occurrenceErrors' field codes are not
      // localized messages (see create-event-series.ts).
      if (e instanceof ApiError && Array.isArray(e.data?.occurrenceErrors)) {
        const byIndex: Record<number, Record<string, string>> = {};
        for (const { index, errors: fieldErrors } of e.data.occurrenceErrors as {
          index: number;
          errors: Record<string, string>;
        }[]) {
          byIndex[index] = fieldErrors;
        }
        setErrors(byIndex);
      }
      toast({
        title: t('toast.error'),
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('form.series.title')}</DialogTitle>
          <DialogDescription>{t('form.series.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {blocks.map((block, index) => {
            const blockErrors = errors[index] ?? {};
            return (
              <div key={index} className="space-y-3 rounded-md border p-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">{t('form.series.period', { number: index + 1 })}</h4>
                  {blocks.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-destructive hover:text-destructive"
                      onClick={() => removeBlock(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Zeile 1: nur Datum (25.09.2026, Nutzervorgabe) — Uhrzeit
                    zieht in die eigene Zeile darunter. */}
                <div>
                  <label className="text-xs text-muted-foreground">{t('form.fields.dateRange')}</label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      type="date"
                      className="w-[150px]"
                      value={block.startDate}
                      onChange={(e) => {
                        const startDate = e.target.value;
                        updateBlock(index, {
                          startDate,
                          endDate: block.endDate || startDate,
                        });
                      }}
                    />
                    <span className="text-muted-foreground">–</span>
                    <Input
                      type="date"
                      className="w-[150px]"
                      value={block.endDate}
                      onChange={(e) => updateBlock(index, { endDate: e.target.value })}
                    />
                  </div>
                  {(blockErrors.startDate || blockErrors.endDate) && (
                    <p className="mt-1 text-xs text-destructive">
                      {blockErrors.startDate ?? blockErrors.endDate}
                    </p>
                  )}
                </div>

                {/* Zeile 2: Uhrzeit + "Während der Geschäftszeiten" (25.09.2026,
                    Nutzervorgabe, korrigiert) — pro Termin frei wählbar, NICHT
                    vom Quell-Event übernommen (siehe Datei-Kommentar oben und
                    create-event-series.ts). Aktivieren leert die Uhrzeit und
                    überspringt ihre Prüfung. Auto-":"-Einfügung wie im
                    Hauptformular (formatTimeInput, EventFormDialog.tsx). */}
                <div>
                  <label className="text-xs text-muted-foreground">{t('form.fields.timeRange')}</label>
                  <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        placeholder="--:--"
                        className="w-[68px]"
                        disabled={block.businessHours}
                        value={block.businessHours ? '' : block.startTime}
                        onChange={(e) => updateBlock(index, { startTime: formatTimeInput(e.target.value) })}
                      />
                      <span className="text-muted-foreground">–</span>
                      <Input
                        type="text"
                        placeholder="--:--"
                        className="w-[68px]"
                        disabled={block.businessHours}
                        value={block.businessHours ? '' : block.endTime}
                        onChange={(e) => updateBlock(index, { endTime: formatTimeInput(e.target.value) })}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={block.businessHours}
                        onCheckedChange={(checked) =>
                          updateBlock(index, {
                            businessHours: checked,
                            ...(checked ? { startTime: '', endTime: '' } : {}),
                          })
                        }
                      />
                      <label className="text-sm font-medium">{t('form.fields.businessHours')}</label>
                    </div>
                  </div>
                  {(blockErrors.startTime || blockErrors.endTime) && (
                    <p className="mt-1 text-xs text-destructive">
                      {blockErrors.startTime ?? blockErrors.endTime}
                    </p>
                  )}
                </div>

                {/* Zeile 3: Geplante Onlinestellung. */}
                <div>
                  <label className="text-xs text-muted-foreground">{t('form.fields.plannedOnlineDate')}</label>
                  <Input
                    type="date"
                    className="mt-1 w-[150px]"
                    value={block.plannedOnlineDate}
                    onChange={(e) => {
                      updateBlock(index, { plannedOnlineDate: e.target.value });
                      checkOnlineDateOnPick(index, e.target.value);
                    }}
                  />
                  {blockErrors.plannedOnlineDate && (
                    <p className="mt-1 text-xs text-destructive">{blockErrors.plannedOnlineDate}</p>
                  )}
                </div>

                {/* Zeile 4: Anmelde-/Zahlungsfrist nebeneinander, je nach
                    VA-Typ (25.09.2026, Nutzervorgabe) — vorher zwei volle
                    Zeilen. */}
                {(source.registrationRequired || source.costEnabled) && (
                  <div className="flex flex-wrap gap-4">
                    {source.registrationRequired && (
                      <div>
                        <label className="text-xs text-muted-foreground">
                          {t('form.fields.registrationDeadline')}
                        </label>
                        <Input
                          type="date"
                          className="mt-1 w-[150px]"
                          value={block.registrationDeadline}
                          onChange={(e) => updateBlock(index, { registrationDeadline: e.target.value })}
                        />
                        {blockErrors.registrationDeadline && (
                          <p className="mt-1 text-xs text-destructive">{blockErrors.registrationDeadline}</p>
                        )}
                      </div>
                    )}

                    {source.costEnabled && (
                      <div>
                        <label className="text-xs text-muted-foreground">{t('form.fields.paymentDeadline')}</label>
                        <Input
                          type="date"
                          className="mt-1 w-[150px]"
                          value={block.paymentDeadline}
                          onChange={(e) => updateBlock(index, { paymentDeadline: e.target.value })}
                        />
                        {blockErrors.paymentDeadline && (
                          <p className="mt-1 text-xs text-destructive">{blockErrors.paymentDeadline}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <Button type="button" variant="outline" size="sm" className="gap-2" onClick={addBlock}>
            <Plus className="h-4 w-4" />
            {t('form.series.addPeriod')}
          </Button>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => submit('initial')}
            disabled={createSeries.isPending}
          >
            {t('actions.save')}
          </Button>
          <Button type="button" onClick={() => submit('in_review')} disabled={createSeries.isPending}>
            {t('actions.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
